import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { remove } from 'lodash-es';
import { GuidelineService, InternalLinkSearchResult, LinksService } from 'shared/domain';
import { RichTextEditorComponent } from 'shared/feature-rich-text-editor';
import {
  ButtonDirective,
  FormFieldImports,
  IconDirective,
  InputDirective,
  LabelDirective,
  PopoverRef,
  PopoverService,
} from 'shared/ui-common';

import { LinkElementType, GuidelineLinkDto } from '@treetop/shared/model';
import { AnchorLinksSearchPopoverComponent } from '@treetop/shared/tt-anchor-link-search';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-anchor-link-edit',
  templateUrl: './anchor-link-edit.component.html',
  styleUrls: ['./anchor-link-edit.component.scss'],
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    FormFieldImports,
    RichTextEditorComponent,

    InputDirective,
    LabelDirective,
    IconDirective,
    MatIconModule,
    ButtonDirective,
  ],
})
export class AnchorLinkEditComponent implements OnInit {
  private formBuilder = new FormBuilder();
  private popoverService = inject(PopoverService);
  private guidelineService = inject(GuidelineService);
  private linksService = inject(LinksService);

  public link = input<GuidelineLinkDto | undefined>(undefined);

  public cancelEdit = output<void>();
  public saveLink = output<GuidelineLinkDto>();

  private linkInputRef = viewChild<HTMLInputElement>('linkInputRef');

  private popover: PopoverRef;

  protected selectedInternalLink = signal<InternalLinkSearchResult | undefined>(undefined);

  protected originalTitle = computed<string>(() => {
    const link = this.selectedInternalLink();
    if (!link) return '';

    const widget = this.guidelineService.getWidget(link.uniqueId);
    if (!widget) return link.title;

    if (widget.widgetType === 'chapter') {
      return widget.title ?? link.title;
    }
    if (widget.widgetType === 'component') {
      return widget.component?.title ?? link.title;
    }

    return link.title;
  });

  protected editLinkForm = this.formBuilder.group({
    title: ['', Validators.required],
    description: [''],
    toUrl: ['', [Validators.required, this.customLinkValidator]],
  });

  public ngOnInit() {
    if (this.link()) {
      this.editLinkForm.setValue({
        title: this.link().title || '',
        description: this.link().description || '',
        toUrl: this.link().toUrl || '',
      });

      if (this.link().id && this.link().toElementType !== LinkElementType.External) {
        this.selectedInternalLink.set({
          uniqueId: this.link().toElementUniqueId || '',
          title: this.link().title || '',
          toElementType: this.link().toElementType || LinkElementType.External,
          toChapterUniqueId: this.link().toChapterUniqueId || undefined,
          externalGuidelineUniqueId: this.link().toElementType === LinkElementType.Guideline ? this.link().toUrl : undefined,
        });
      }
    }
  }

  private customLinkValidator(control: FormControl): ValidationErrors | null {
    const urlPattern = /^(http:\/\/|https:\/\/).*/;
    const value = control.value;
    if (!urlPattern.test(value)) {
      return { customError: true };
    }

    return null;
  }

  protected updateDescription(description: string) {
    this.editLinkForm.controls.description.setValue(description);
  }

  protected handleSaveButtonClicked() {
    const link: GuidelineLinkDto = {
      ...this.link(),
      title: this.editLinkForm.value.title || '',
      description: this.editLinkForm.value.description || '',
      toUrl: this.selectedInternalLink() ? undefined : this.editLinkForm.value.toUrl,
      toElementType: this.selectedInternalLink()?.toElementType ?? LinkElementType.External,
      toElementUniqueId: this.selectedInternalLink()?.uniqueId ?? undefined,
      toChapterUniqueId: this.selectedInternalLink()?.uniqueId ?? undefined,
    };

    if (this.selectedInternalLink()?.toElementType === LinkElementType.Guideline) {
      link.toUrl = this.selectedInternalLink().externalGuidelineUniqueId;
      link.toElementUniqueId = undefined;
    } else if (this.selectedInternalLink()?.externalGuidelineUniqueId) {
      link.toUrl = `${this.selectedInternalLink().externalGuidelineUniqueId}#${this.selectedInternalLink().uniqueId}`;
    }

    this.saveLink.emit(link);
  }

  private showAnchorLinksPopover() {
    if (this.popover) {
      return;
    }

    this.popover = this.popoverService.open(AnchorLinksSearchPopoverComponent, this.linkInputRef(), {
      width: 432,
      panelClass: 'anchor-links-popover',
      positionStrategy: 'start',
      hasBackdrop: false,
      data: {
        guideline: this.guidelineService.guideline,
        searchFormControl: this.editLinkForm.controls.toUrl,
      },
      providers: [{ provide: LinksService, useValue: this.linksService }],
    });

    this.popover
      .afterClosed()
      .pipe()
      .subscribe((link) => {
        if (link) {
          this.internalLinkSelected(link);
        }

        this.popover = null;
      });
  }

  protected internalLinkSelected(result: InternalLinkSearchResult) {
    if (result) {
      this.selectedInternalLink.set(result);
      this.editLinkForm.controls.title.setValue(result.title);
    }
  }

  protected removeInternalLink() {
    this.selectedInternalLink.set(undefined);
    this.editLinkForm.controls.toUrl.setValue('');
    this.editLinkForm.controls.toUrl.markAsDirty();
    this.editLinkForm.controls.toUrl.markAsTouched();
    this.editLinkForm.controls.title.setValue('');
    this.editLinkForm.controls.title.markAsDirty();
    this.editLinkForm.controls.title.markAsTouched();
  }

  protected handleToUrlChanged() {
    const toUrl = this.editLinkForm.controls.toUrl.value || '';

    const isExternalUrl = !this.editLinkForm.controls.toUrl.errors?.customError;

    if (!isExternalUrl && toUrl.length >= 3 && !this.popover) {
      this.showAnchorLinksPopover();
    } else if (this.popover && (isExternalUrl || toUrl.length < 3)) {
      this.popover.close();
      this.popover = null;
    }
  }

  protected readonly LinkElementType = LinkElementType;
  protected readonly remove = remove;
}
